import { useCallback, useState } from "react";

import { getProgramTemplates } from "generated/api/program-templates";
import { useCurrentClient } from "hooks/api/use-clients";
import toast from "components/partials/toast/toast";

import { ProgramTemplateCreateRequest, TouchpointCreateRequestType } from "generated/schemas";
import type { AddNewProgramTemplateFormData } from "containers/admin/clients/client/program-templates-page/program-templates-page.types";

import { LoadingButtonState } from "ui/button";

export const useCreateProgramTemplate = (onSuccess?: () => void) => {
  const currentClient = useCurrentClient();
  const [submitState, setSubmitState] = useState<LoadingButtonState>(LoadingButtonState.Default);

  const resetState = useCallback(() => {
    setSubmitState(LoadingButtonState.Default);
  }, []);

  const createProgramTemplate = useCallback(
    async (data: AddNewProgramTemplateFormData, templateType: TouchpointCreateRequestType) => {
      if (!currentClient?.id) {
        toast.error({
          data: {
            title: "Failed to create program template",
            message: "Client ID is missing",
          },
        });
        return;
      }

      setSubmitState(LoadingButtonState.Loading);

      try {
        const programTemplatesApi = getProgramTemplates();

        const isEmail = templateType === TouchpointCreateRequestType.EMAIL;

        const request: ProgramTemplateCreateRequest = {
          name: data.name,
          type: templateType,
          applyClientTheme: isEmail ? data.applyClientTheme : undefined,
        };

        await programTemplatesApi.createProgramTemplate({ clientId: currentClient.id }, request);

        setSubmitState(LoadingButtonState.Success);
        toast.success({
          data: {
            title: "Created template",
          },
        });

        onSuccess?.();
      } catch (error) {
        console.error("Failed to create program template:", error);
        setSubmitState(LoadingButtonState.Error);
        toast.error({
          data: {
            title: "Failed to create program template",
          },
        });
      }
    },
    [currentClient, onSuccess],
  );

  return {
    createProgramTemplate,
    submitState,
    resetState,
  };
};
